import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TurnStileDarkPlaceholder from '../../assets/images/TurnStileDarkPlaceholder.png';
import TurnStileLightPlaceholder from '../../assets/images/TurnStileLightPlaceholder.png';
import useTheme from '../../../../hooks/ui/useTheme';
import CircularProgress from '@mui/material/CircularProgress';

const MessageType = {
  TurnStileToken: 'turnstile-token',
  ParentOrigin: 'parent-origin',
  TurnStileLoaded: 'turnstile-loaded',
};

interface TurnStileIframeProps {
  onVerify: (token: string) => void;
}

const TurnStile = ({ onVerify }: TurnStileIframeProps) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      switch (event?.data?.type) {
        case MessageType.TurnStileLoaded:
          setTimeout(() => setLoading(false), 500);
          break;
        case MessageType.TurnStileToken:
          onVerify(event.data.payload.token);
          break;
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const renderPlaceholder = useMemo(
    () =>
      loading && (
        <div className="absolute inset-0 bg-brand-background-primary">
          <div className="flex items-center h-full w-[300px] relative">
            <img
              src={theme.isDarkMode ? TurnStileDarkPlaceholder : TurnStileLightPlaceholder}
              alt="TurnStile"
              className="w-full h-full absolute inset-0"
            />
            <div className="relative px-[17px] flex items-center gap-[10.5px]">
              <CircularProgress
                size={30}
                style={{
                  color: '#1A9F31',
                }}
              />
              <p className="text-[#272727] dark:text-white text-[13px] font-normal">Verifying...</p>
            </div>
          </div>
        </div>
      ),
    [loading, theme.isDarkMode],
  );

  const queryParams = {
    language: i18n.language,
    theme: theme.isDarkMode ? 'dark' : 'light',
    type: MessageType.TurnStileToken,
  };

  const url = `${import.meta.env.VITE_STOREFRONT_TURNSTILE_DOMAIN}/?${new URLSearchParams(queryParams).toString()}`;

  const sendMessageToIframe = () => {
    setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage({ type: MessageType.ParentOrigin }, url);
    }, 200);
  };

  const widget = useMemo(() => {
    return (
      <iframe
        ref={iframeRef}
        src={url}
        className="w-full h-[65px] mt-md scrollbar-hide"
        referrerPolicy="no-referrer-when-downgrade"
        style={{
          border: 'none',
          overflow: 'hidden',
        }}
        onLoad={() => sendMessageToIframe()}
      />
    );
  }, [url]);

  return (
    <div className="relative w-full h-[65px]">
      {widget}
      {renderPlaceholder}
    </div>
  );
};

export default TurnStile;
